import React, { Component } from 'react';
import { DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Selection, IconButton, CommandButton, Panel, PrimaryButton, DefaultButton, PanelType, TextField, MessageBar, MessageBarType, Dialog, DialogType, DialogFooter } from '@fluentui/react';
import { IUserRoleAssignment, IDBRole, ISetRolesPayload } from './types';
import NoRecordsFound from '../../../../../core/components/NoRecordsFound';
import Template from '../../../../../core/libraries/Template';
import { deleteUserRoles, getAllUserRoleAssignments, setRoles, unsetRoles, updateUserRoles } from './services';
import { isArray } from 'lodash';

interface IProps {
    dbRoles?: IDBRole[] | null;
}

interface IState {
    userRoleAssignments: IUserRoleAssignment[];
    selectedUser: IUserRoleAssignment | null;
    isOpenPanel: boolean;
    editingUser: IUserRoleAssignment | null;
    draggedRole: IDBRole | null;
    selectionDetails?: string;
    panelUserId: string;
    panelSelectedRoles: IDBRole[];
    errorMessage: string;
    confirmDialog: {
        isOpen: boolean;
        title: string;
        message: string;
        onConfirm: () => void;
    };
}

class UserHasRoleTable extends Component<IProps, IState> {
    private _selection: Selection;

    constructor(props: IProps) {
        super(props);

        this._selection = new Selection({
            onSelectionChanged: () => {
                this.setState({
                    selectionDetails: this._getSelectionDetails(),
                });
            },
            getKey: this._getKey,
        });

        this.state = {
            userRoleAssignments: [],
            selectedUser: null,
            isOpenPanel: false,
            editingUser: null,
            draggedRole: null,
            panelUserId: '',
            panelSelectedRoles: [],
            errorMessage: '',
            confirmDialog: {
                isOpen: false,
                title: '',
                message: '',
                onConfirm: () => { }
            }
        };
    }

    async componentDidMount() {
        const roleAssignments = await getAllUserRoleAssignments()
        this.setState({ userRoleAssignments: !!roleAssignments && isArray(roleAssignments) ? roleAssignments : [] });
    }

    private _getSelectionDetails(): string {
        const selectionCount = this._selection.getSelectedCount();

        switch (selectionCount) {
            case 0:
                return 'No items selected';
            case 1:
                return '1 item selected: ' + (this._selection.getSelection()[0] as IDBRole).name;
            default:
                return `${selectionCount} items selected`;
        }
    }

    private _getKey(item: any, index?: number): string {
        return item.id?.toString() || index?.toString() || '0';
    }

    // Drag and Drop handlers
    private _onDragStart = (item: IDBRole) => (e: React.DragEvent) => {
        this.setState({ draggedRole: item });
        e.dataTransfer.setData('text/plain', item.id);
        e.dataTransfer.effectAllowed = 'copy';
    };

    private _onDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    private _onDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const { draggedRole, selectedUser, userRoleAssignments } = this.state;

        if (draggedRole && selectedUser && !selectedUser.roles.find(role => role.id === draggedRole.id)) {
            const updatedAssignments = userRoleAssignments.map(user =>
                user.peopleId === selectedUser.peopleId
                    ? { ...user, roles: [...user.roles, draggedRole] }
                    : user
            );

            const updatedSelectedUser = updatedAssignments.find(user => user.peopleId === selectedUser.peopleId);

            this.setState({
                userRoleAssignments: updatedAssignments,
                selectedUser: updatedSelectedUser || null,
                draggedRole: null
            });
        }
    };

    private _onRemoveRole = (userPeopleId: string, roleToRemove: IDBRole) => {
        this.setState({
            confirmDialog: {
                isOpen: true,
                title: 'Remove Role',
                message: `Are you sure you want to remove the role "${roleToRemove.name}" from this user?`,
                onConfirm: async () => {
                    const { userRoleAssignments, selectedUser } = this.state;

                    const unsetRolesPayload: ISetRolesPayload = {
                        peopleId: userPeopleId,
                        roleIds: [parseInt(roleToRemove.id)]
                    };

                    const callUnsetRoles = await unsetRoles(unsetRolesPayload);
                    if (!callUnsetRoles) {
                        this.setState({ errorMessage: 'Failed to remove the role from the user. Please try again.' });
                        this._closeConfirmDialog();
                        return;
                    }

                    const updatedAssignments = userRoleAssignments.map(user =>
                        user.peopleId === userPeopleId
                            ? { ...user, roles: user.roles.filter(role => role.id !== roleToRemove.id) }
                            : user
                    );

                    const updatedSelectedUser = selectedUser && selectedUser.peopleId === userPeopleId
                        ? updatedAssignments.find(user => user.peopleId === userPeopleId) || null
                        : selectedUser;

                    this.setState({
                        userRoleAssignments: updatedAssignments,
                        selectedUser: updatedSelectedUser
                    });
                    this._closeConfirmDialog();
                }
            }
        });
    };

    private _onAddUser = (): void => {
        this.setState({
            isOpenPanel: true,
            editingUser: null,
            panelUserId: '',
            panelSelectedRoles: [],
            errorMessage: ''
        });
    };

    private _onEditUser = (user: IUserRoleAssignment): void => {
        this.setState({
            isOpenPanel: true,
            editingUser: user,
            panelUserId: user.peopleId,
            panelSelectedRoles: [...user.roles],
            errorMessage: ''
        });
    };

    private _onRemoveSelectedUsers = (): void => {
        const selectedUsers = this._selection.getSelection() as IUserRoleAssignment[];
        if (selectedUsers.length > 0) {
            this.setState({
                confirmDialog: {
                    isOpen: true,
                    title: 'Remove Selected Users',
                    message: `Are you sure you want to remove ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}? This will delete all their role assignments.`,
                    onConfirm: async () => {
                        const { userRoleAssignments } = this.state;
                        const selectedIds = selectedUsers.map(user => user.peopleId);
                        const updatedAssignments = userRoleAssignments.filter(user => !selectedIds.includes(user.peopleId));

                        const callDeleteUserRoles = await deleteUserRoles(selectedIds);
                        if (!callDeleteUserRoles) {
                            this.setState({ errorMessage: 'Failed to delete selected users. Please try again.' });
                            this._closeConfirmDialog();
                            return;
                        }

                        this.setState({
                            userRoleAssignments: updatedAssignments,
                            selectedUser: null
                        });
                        this._closeConfirmDialog();
                    }
                }
            });
        }
    };

    private _closeConfirmDialog = (): void => {
        this.setState({
            confirmDialog: {
                isOpen: false,
                title: '',
                message: '',
                onConfirm: () => { }
            }
        });
    };

    dismissPanel = () => {
        this.setState({
            isOpenPanel: false,
            editingUser: null,
            panelUserId: '',
            panelSelectedRoles: [],
            errorMessage: ''
        });
    };

    private _onSaveUser = async (): Promise<void> => {
        const { panelUserId, panelSelectedRoles, editingUser, userRoleAssignments } = this.state;

        // Validation
        if (!panelUserId.trim()) {
            this.setState({ errorMessage: 'User ID is required' });
            return;
        }

        // Check if user already exists (only for new users)
        if (!editingUser && userRoleAssignments.some(u => u.peopleId === panelUserId)) {
            this.setState({ errorMessage: 'User ID already exists' });
            return;
        }
        const setRolePayLoad: ISetRolesPayload = {
            peopleId: panelUserId,
            roleIds: panelSelectedRoles.map(r => parseInt(r.id))
        };

        if (editingUser) {
            // Update existing user
            const updatedAssignments = userRoleAssignments.map(user =>
                user.peopleId === editingUser.peopleId
                    ? { ...user, roles: panelSelectedRoles }
                    : user
            );



            const callUpdateUserHasRoles = await updateUserRoles(setRolePayLoad);
            if (!callUpdateUserHasRoles) {
                this.setState({ errorMessage: 'Failed to update roles for the user. Please try again.' });
                return;
            }

            this.setState({
                userRoleAssignments: updatedAssignments,
                isOpenPanel: false,
                editingUser: null,
                panelUserId: '',
                panelSelectedRoles: [],
                errorMessage: ''
            });
        } else {
            // Add new user
            const newUser: IUserRoleAssignment = {
                peopleId: panelUserId,
                roles: panelSelectedRoles
            };

            const callAddNewUserHasRoles = await setRoles(setRolePayLoad);
            if (!callAddNewUserHasRoles) {
                this.setState({ errorMessage: 'Failed to add roles to the new user. Please try again.' });
                return;
            }

            this.setState({
                userRoleAssignments: [...userRoleAssignments, newUser],
                isOpenPanel: false,
                editingUser: null,
                panelUserId: '',
                panelSelectedRoles: [],
                errorMessage: ''
            });
        }
    };

    private _onPanelAddRole = (role: IDBRole): void => {
        const { panelSelectedRoles } = this.state;
        if (!panelSelectedRoles.find(r => r.id === role.id)) {
            this.setState({
                panelSelectedRoles: [...panelSelectedRoles, role]
            });
        }
    };

    private _onPanelRemoveRole = (role: IDBRole): void => {
        const { panelSelectedRoles } = this.state;
        this.setState({
            panelSelectedRoles: panelSelectedRoles.filter(r => r.id !== role.id)
        });
    };

    private _onPanelDragStart = (item: IDBRole) => (e: React.DragEvent) => {
        this.setState({ draggedRole: item });
        e.dataTransfer.setData('text/plain', item.id);
        e.dataTransfer.effectAllowed = 'copy';
    };

    private _onPanelDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'copy';
    };

    private _onPanelDrop = (e: React.DragEvent) => {
        e.preventDefault();
        const { draggedRole } = this.state;

        if (draggedRole) {
            this._onPanelAddRole(draggedRole);
            this.setState({ draggedRole: null });
        }
    };

    // Columns for user role assignments table
    userRoleColumns: IColumn[] = [
        {
            key: 'peopleId',
            name: 'User ID',
            ariaLabel: 'User ID',
            fieldName: 'peopleId',
            minWidth: 200,
            maxWidth: 350,
            onColumnClick: undefined,
            onRender: (item: IUserRoleAssignment) => {
                return (
                    Template.renderReadOnlyBeautyPeople([item.peopleId])
                );
            }
        },
        {
            key: 'roles',
            name: 'Assigned roles',
            ariaLabel: 'Assigned roles',
            minWidth: 400,
            maxWidth: 600,
            onColumnClick: undefined,
            onRender: (item: IUserRoleAssignment) => {
                return (
                    <div style={{ padding: '8px 0' }}>
                        {item.roles.length === 0 ? (
                            <span style={{ color: '#666', fontStyle: 'italic' }}>No roles assigned</span>
                        ) : (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                {item.roles.map((role) => (
                                    <div
                                        key={role.id}
                                        style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            backgroundColor: '#e1f5fe',
                                            color: '#0277bd',
                                            padding: '4px 8px',
                                            borderRadius: '12px',
                                            fontSize: '12px',
                                            border: '1px solid #b3e5fc'
                                        }}
                                    >
                                        <span style={{ marginRight: '4px' }}>{role.name}</span>
                                        <IconButton
                                            iconProps={{ iconName: 'Cancel' }}
                                            title="Remove Role"
                                            ariaLabel="Remove Role"
                                            onClick={() => this._onRemoveRole(item.peopleId, role)}
                                            styles={{
                                                root: {
                                                    width: '16px',
                                                    height: '16px',
                                                    minWidth: '16px'
                                                },
                                                icon: {
                                                    color: '#d13438',
                                                    fontSize: '10px'
                                                }
                                            }}
                                        />
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            },
        },
        {
            key: 'roleCount',
            name: 'Role Count',
            ariaLabel: 'Number of roles assigned',
            minWidth: 80,
            maxWidth: 100,
            onColumnClick: undefined,
            onRender: (item: IUserRoleAssignment) => {
                return (
                    <span style={{
                        fontWeight: '600',
                        color: item.roles.length > 0 ? '#107c10' : '#666'
                    }}>
                        {item.roles.length}
                    </span>
                );
            },
        },
        {
            key: 'actions',
            name: 'Actions',
            ariaLabel: 'Actions',
            minWidth: 100,
            maxWidth: 100,
            onColumnClick: undefined,
            onRender: (item: IUserRoleAssignment) => {
                return (
                    <div style={{ display: 'flex', gap: '4px' }}>
                        <IconButton
                            iconProps={{ iconName: 'Edit' }}
                            title="Edit User"
                            ariaLabel="Edit User"
                            onClick={() => this._onEditUser(item)}
                        />
                    </div>
                );
            },
        },
    ];

    private _renderHeaderActions = (): JSX.Element => {
        return (
            <div style={{
                height: '80px',
                backgroundColor: '#f8f9fa',
                borderBottom: '1px solid #edebe9',
                padding: '10px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
            }}>
                <h3 style={{ margin: '0 0 8px 0' }}>FEBER roles Definition in Database</h3>
                <div>
                    <CommandButton
                        iconProps={{ iconName: 'AddFriend' }}
                        text="Add User"
                        onClick={this._onAddUser}
                        styles={{
                            root: { color: '#0078d4', fontWeight: 600 },
                            icon: { color: '#0078d4' }
                        }}
                    />
                    <CommandButton
                        iconProps={{ iconName: 'RemoveFriend' }}
                        text="Remove Selected User(s)"
                        onClick={this._onRemoveSelectedUsers}
                        styles={{
                            root: { color: '#d13438', fontWeight: 600 },
                            icon: { color: '#d13438' }
                        }}
                    />
                </div>
            </div>
        );
    };

    render() {
        const { userRoleAssignments, isOpenPanel, editingUser, panelUserId, panelSelectedRoles, errorMessage, confirmDialog } = this.state;
        const { dbRoles } = this.props;
        const availableDbRoles = dbRoles && dbRoles.length > 0 ? dbRoles : [];

        return (
            <div style={{ height: '600px', display: 'block' }}>
                {this._renderHeaderActions()}

                {userRoleAssignments.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                        <NoRecordsFound />
                    </div>
                ) : (
                    <div style={{
                        height: '500px',
                        overflow: 'auto',
                        border: '1px solid #edebe9',
                        display: 'block',
                        position: 'relative'
                    }}>
                        <DetailsList
                            items={userRoleAssignments}
                            columns={this.userRoleColumns}
                            selectionMode={SelectionMode.multiple}
                            setKey="userRoles"
                            layoutMode={DetailsListLayoutMode.justified}
                            isHeaderVisible={true}
                            selection={this._selection}
                            selectionPreservedOnEmptyClick={true}
                            styles={{
                                root: {
                                    '.ms-DetailsHeader': {
                                        position: 'sticky',
                                        top: 0,
                                        zIndex: 100,
                                        backgroundColor: '#ffffff'
                                    }
                                }
                            }}
                        />
                    </div>
                )}

                {/* Add/Edit User Panel */}
                <Panel
                    isOpen={isOpenPanel}
                    onDismiss={this.dismissPanel}
                    headerText={editingUser ? `Edit User: ${editingUser.peopleId}` : "Add New User"}
                    closeButtonAriaLabel="Close"
                    onRenderFooterContent={() => (
                        <div>
                            <PrimaryButton onClick={this._onSaveUser} styles={{ root: { marginRight: 8 } }}>
                                Save
                            </PrimaryButton>
                            <DefaultButton onClick={this.dismissPanel}>Cancel</DefaultButton>
                        </div>
                    )}
                    isFooterAtBottom={true}
                    customWidth='500px'
                    type={PanelType.customNear}
                    isLightDismiss
                >
                    {errorMessage && (
                        <MessageBar
                            messageBarType={MessageBarType.error}
                            isMultiline={false}
                            onDismiss={() => this.setState({ errorMessage: '' })}
                            dismissButtonAriaLabel="Close"
                            styles={{ root: { marginBottom: 15 } }}
                        >
                            {errorMessage}
                        </MessageBar>
                    )}

                    <TextField
                        label="User ID (People ID)"
                        value={panelUserId}
                        onChange={(e, newValue) => this.setState({ panelUserId: newValue || '', errorMessage: '' })}
                        placeholder="Enter user ID (e.g., uib80001)"
                        required
                        disabled={!!editingUser}
                        styles={{ root: { marginBottom: 20 } }}
                    />

                    <div style={{ marginBottom: 15 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                            Assigned roles <span style={{ color: '#a4262c' }}>*</span>
                        </div>
                        <div
                            onDragOver={this._onPanelDragOver}
                            onDrop={this._onPanelDrop}
                            style={{
                                minHeight: '80px',
                                padding: '10px',
                                border: '2px dashed #d1d1d1',
                                borderRadius: '4px',
                                backgroundColor: '#f8f9fa',
                                marginTop: '8px'
                            }}
                        >
                            {panelSelectedRoles.length === 0 ? (
                                <div style={{ textAlign: 'center', color: '#666', padding: '20px', fontStyle: 'italic' }}>
                                    Drag roles here or click below to add
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                                    {panelSelectedRoles.map((role) => (
                                        <div
                                            key={role.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                backgroundColor: '#e1f5fe',
                                                color: '#0277bd',
                                                padding: '6px 10px',
                                                borderRadius: '12px',
                                                fontSize: '13px',
                                                border: '1px solid #b3e5fc'
                                            }}
                                        >
                                            <span style={{ marginRight: '6px' }}>{role.name}</span>
                                            <IconButton
                                                iconProps={{ iconName: 'Cancel' }}
                                                title="Remove Role"
                                                ariaLabel="Remove Role"
                                                onClick={() => this._onPanelRemoveRole(role)}
                                                styles={{
                                                    root: {
                                                        width: '18px',
                                                        height: '18px',
                                                        minWidth: '18px'
                                                    },
                                                    icon: {
                                                        color: '#d13438',
                                                        fontSize: '10px'
                                                    }
                                                }}
                                            />
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div style={{ marginTop: 20 }}>
                        <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '8px' }}>
                            Available roles
                        </div>
                        <div style={{
                            maxHeight: '300px',
                            overflow: 'auto',
                            border: '1px solid #d1d1d1',
                            borderRadius: '4px',
                            marginTop: '8px'
                        }}>
                            {availableDbRoles.map((role: IDBRole) => {
                                const isAdded = panelSelectedRoles.some(r => r.id === role.id);
                                return (
                                    <div
                                        key={role.id}
                                        draggable={!isAdded}
                                        onDragStart={this._onPanelDragStart(role)}
                                        style={{
                                            padding: '10px',
                                            borderBottom: '1px solid #edebe9',
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            backgroundColor: isAdded ? '#f3f2f1' : '#fff',
                                            cursor: isAdded ? 'default' : 'grab',
                                            opacity: isAdded ? 0.5 : 1
                                        }}
                                    >
                                        <div>
                                            <div style={{ fontWeight: 600, fontSize: '14px', marginBottom: '4px' }}>
                                                {role.name}
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#605e5c' }}>
                                                {role.description}
                                            </div>
                                        </div>
                                        {!isAdded && (
                                            <IconButton
                                                iconProps={{ iconName: 'Add' }}
                                                title="Add Role"
                                                ariaLabel="Add Role"
                                                onClick={() => this._onPanelAddRole(role)}
                                                styles={{
                                                    root: {
                                                        color: '#0078d4'
                                                    }
                                                }}
                                            />
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Panel>

                {/* Confirmation Dialog */}
                <Dialog
                    hidden={!confirmDialog.isOpen}
                    onDismiss={this._closeConfirmDialog}
                    dialogContentProps={{
                        type: DialogType.normal,
                        title: confirmDialog.title,
                        subText: confirmDialog.message
                    }}
                    modalProps={{
                        isBlocking: true,
                        styles: { main: { maxWidth: 450 } }
                    }}
                >
                    <DialogFooter>
                        <PrimaryButton
                            onClick={confirmDialog.onConfirm}
                            text="Confirm"
                            styles={{
                                root: { backgroundColor: '#d13438', borderColor: '#d13438' },
                                rootHovered: { backgroundColor: '#a4262c', borderColor: '#a4262c' }
                            }}
                        />
                        <DefaultButton onClick={this._closeConfirmDialog} text="Cancel" />
                    </DialogFooter>
                </Dialog>
            </div>
        );
    }
}

export default UserHasRoleTable;